import * as React from 'react';

import { LegalAdminApiError } from '@/api/legal-admin-client';
import { useForceResignatureCgu } from '@/api/use-legal-admin';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Label } from '@/components/ui/label';

// =============================================================================
// force-resignature-action.tsx — Action admin « Forcer la resignature » (R15-GOV-04).
// =============================================================================
// DBU §2.9 l.2136-2144 : action ciblée par fournisseur, saisie d'un MOTIF OBLIGATOIRE,
// effet immédiat, journalisation horodatée. Bouton déclencheur + modale de confirmation DS
// (ConfirmDialog) portant un textarea motif LABELLISÉ. Réutilisé par DEUX surfaces (vue
// non-signataires §2.9 + fiche fournisseur A-04). L'AUTORITÉ reste l'API : le motif est
// re-validé serveur (400 si vide), le blocage front (Confirmer désactivé) n'est qu'un garde-fou.
//
// PII : le motif ne quitte le front que dans le CORPS POST (jamais URL, jamais loggé), le
// hook/client le transmettent tel quel, aucun `console`. WCAG 2.1 AA : `<label htmlFor>` sur
// le textarea, `aria-required`, dialog accessible (role/aria/focus/Échap portés par ConfirmDialog),
// feedback via role live. Anti-LLMism éditorial : aucun em-dash de substitution dans les textes FR.

/** Traduit une erreur de force-resignature en message utilisateur PAR CODE (jamais le message serveur brut). */
function forceResignatureErrorMessage(error: unknown): string {
  if (error instanceof LegalAdminApiError && error.status === 400) {
    return 'Un motif est requis pour forcer la resignature.';
  }
  return "La resignature n'a pas pu être forcée. Réessayez plus tard.";
}

export interface ForceResignatureActionProps {
  readonly siret: string;
}

/**
 * Bouton « Forcer la resignature » + modale motif. État LOCAL (in-memory React) : ouverture,
 * motif saisi, erreur serveur, feedback de succès. Le bouton Confirmer est désactivé tant que le
 * motif (trim) est vide (obligation DBU) et pendant l'appel. Après succès, le hook invalide les
 * lectures fournisseurs (l'état ResignatureRequise des contacts apparaît sans action de l'utilisateur).
 */
export function ForceResignatureAction({
  siret,
}: ForceResignatureActionProps): React.JSX.Element {
  const force = useForceResignatureCgu();
  const [open, setOpen] = React.useState(false);
  const [motif, setMotif] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [feedback, setFeedback] = React.useState<string | null>(null);
  // `useId` garantit un id de textarea UNIQUE même si plusieurs actions coexistent (une par ligne
  // du tableau non-signataires), sans doublon d'`id`/`htmlFor` (WCAG 4.1.1).
  const motifId = React.useId();

  const close = (): void => {
    setOpen(false);
    setMotif('');
    setError(null);
  };

  const onConfirm = (): void => {
    setError(null);
    force.mutate(
      { siret, motif: motif.trim() },
      {
        onSuccess: () => {
          setFeedback('Resignature forcée.');
          close();
        },
        onError: (e) => setError(forceResignatureErrorMessage(e)),
      },
    );
  };

  return (
    <div className="flex flex-col items-start gap-1">
      <Button
        type="button"
        variant="outline"
        size="sm"
        aria-label={`Forcer la resignature des CGU de ${siret}`}
        onClick={() => {
          setFeedback(null);
          setError(null);
          setMotif('');
          setOpen(true);
        }}
      >
        Forcer la resignature
      </Button>
      {feedback !== null ? (
        <p role="status" className="text-xs text-muted-foreground">
          {feedback}
        </p>
      ) : null}

      <ConfirmDialog
        open={open}
        title="Forcer la resignature des CGU ?"
        description="Les interlocuteurs actifs de ce fournisseur devront à nouveau accepter les CGU. L'effet est immédiat et l'action est tracée (motif journalisé)."
        confirmLabel={force.isPending ? 'Application…' : 'Forcer la resignature'}
        confirmDisabled={motif.trim() === '' || force.isPending}
        onConfirm={onConfirm}
        onCancel={close}
      >
        <ForceResignatureMotifField id={motifId} value={motif} onChange={setMotif} />
        {error !== null ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </ConfirmDialog>
    </div>
  );
}

/**
 * Champ « motif » OBLIGATOIRE (textarea labellisée). `htmlFor`/`id` natif (WCAG 1.3.1/4.1.2),
 * `required` + `aria-required`, focus déplacé sur le champ à l'ouverture (le bouton Confirmer étant
 * désactivé tant que le motif est vide, il ne capte pas le focus). Parité `ReasonField` (fiche A-04).
 */
function ForceResignatureMotifField({
  id,
  value,
  onChange,
}: {
  readonly id: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
}): React.JSX.Element {
  const ref = React.useRef<HTMLTextAreaElement | null>(null);
  React.useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>Motif de la resignature (obligatoire)</Label>
      <textarea
        id={id}
        ref={ref}
        value={value}
        required
        aria-required="true"
        rows={3}
        onChange={(event) => onChange(event.target.value)}
        className="min-h-[6rem] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      />
    </div>
  );
}
